/**
 * Builds the v2-pipeline plan for daily-only "Move Between Days".
 * `CHANGE_DATE_DAILY` must run before `ASSIGN_DAILY` — BE
 * `ValidateNewStopsDaily` rejects an assign on the target date until
 * `wo.service_date` has flipped. No source-date unassign: the change-date
 * mutation already removes the WO from the source. The atomic master+daily
 * shape lives on `v1-bulk` strategy via `bulkChangeDate` (`includeMaster:true`).
 */
import type {
  ChangeDateIntent,
  Deps,
  OperationPlan,
  PipelineStep,
} from 'types/modules/routing'
import type { CseId, WorkOrderId } from 'types/entities/shared/ids'
import { isDailyStop, isOnRequestStop } from 'types/modules/routing'

import { makeAssignDailyStep } from '../steps/assignDaily'
import { makeChangeDateDailyStep } from '../steps/changeDateDaily'
import { makePreflightStep } from '../steps/preflight'
import { makeSequenceDailyStep } from '../steps/sequenceDaily'
import { makeVerifyStep } from '../steps/verify'

const STRATEGY_ID = 'v2-pipeline' as const

const collectIds = (
  intent: ChangeDateIntent,
): {
  readonly workOrderIds: ReadonlyArray<WorkOrderId>
  readonly cseIds: ReadonlyArray<CseId>
} => {
  const workOrderIds: WorkOrderId[] = []
  const cseIds: CseId[] = []
  const seenWo = new Set<string>()
  const seenCse = new Set<string>()
  for (const stop of intent.stops) {
    if (isDailyStop(stop) || isOnRequestStop(stop)) {
      const wo = stop.workOrderId
      if (wo && !seenWo.has(wo)) {
        seenWo.add(wo)
        workOrderIds.push(wo)
      }
      if (isDailyStop(stop) && stop.configuredServiceEventId) {
        const cse = stop.configuredServiceEventId
        if (!seenCse.has(cse)) {
          seenCse.add(cse)
          cseIds.push(cse)
        }
      }
    }
  }
  return { workOrderIds, cseIds }
}

export const buildChangeDatePipeline = (
  intent: ChangeDateIntent,
  deps: Deps,
): OperationPlan => {
  const steps: PipelineStep[] = []
  const { workOrderIds } = collectIds(intent)

  steps.push(makePreflightStep({ intent }, deps) as PipelineStep)

  if (workOrderIds.length > 0) {
    steps.push(
      makeChangeDateDailyStep(
        {
          routeId: intent.routeId,
          fromServiceDate: intent.fromServiceDate,
          toServiceDate: intent.toServiceDate,
          workOrderIds,
        },
        deps,
      ) as PipelineStep,
      makeAssignDailyStep(
        {
          routeId: intent.routeId,
          serviceDate: intent.toServiceDate,
          workOrderIds,
        },
        deps,
      ) as PipelineStep,
    )
  }

  if (intent.allOrderedStops && intent.allOrderedStops.length > 0) {
    steps.push(
      makeSequenceDailyStep(
        {
          routeId: intent.routeId,
          serviceDate: intent.toServiceDate,
          orderedStops: intent.allOrderedStops,
        },
        deps,
      ) as PipelineStep,
    )
  }

  steps.push(
    makeVerifyStep(
      {
        intent,
        expectedAssignedWorkOrderIds: workOrderIds.map(String),
      },
      deps,
    ) as PipelineStep,
  )

  return {
    strategy: STRATEGY_ID,
    intent,
    steps,
  }
}
